import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import GlobalNavbarEN from '../../../../components/feature/GlobalNavbarEN';
import FooterEN from '../../../home/components/FooterEN';

export default function BillboardInvestmentTaxBenefitsGuide() {
  useEffect(() => {
    document.title = 'Billboard Investment Tax Benefits (U.S.) | Las Vegas Guide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn common U.S. billboard investment tax benefits, depreciation basics, and planning tips for Las Vegas. Educational overview with ranges, not advice.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <GlobalNavbarEN />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-black text-white text-xs font-semibold rounded-full">
                Investment Insights
              </span>
              <span className="ml-3 text-sm text-gray-500">February 5, 2026</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-black mb-6 leading-tight">
              Billboard Investment Tax Benefits (U.S.): A Practical Guide for Las Vegas
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Billboard investments can offer various tax advantages under U.S. tax law, but the specifics depend heavily on individual circumstances, entity structure, and current regulations. This guide provides an educational overview of common tax topics investors explore when considering billboard assets in Las Vegas.
            </p>
            
            {/* Disclaimer */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-8">
              <p className="text-sm text-gray-800 font-semibold mb-2">
                ⚠️ Important Disclaimer
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                This content is for educational purposes only and does not constitute tax, legal, or financial advice. Tax laws are complex and change frequently. Always consult a qualified CPA, tax attorney, or financial advisor before making investment decisions or implementing tax strategies.
              </p>
            </div>

            {/* Table of Contents */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-lg font-bold text-black mb-4">Table of Contents</h2>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <a href="#why-tax-planning" className="hover:text-black transition-colors">Why Tax Planning Matters for Billboard Investors</a></li>
                <li>• <a href="#depreciation-basics" className="hover:text-black transition-colors">Depreciation Basics (Conceptual Overview)</a></li>
                <li>• <a href="#typical-topics" className="hover:text-black transition-colors">Typical Tax Topics Investors Ask About</a></li>
                <li>• <a href="#example-scenarios" className="hover:text-black transition-colors">Example Scenarios</a></li>
                <li>• <a href="#prepare-cpa" className="hover:text-black transition-colors">What to Prepare Before Talking to a CPA</a></li>
                <li>• <a href="#faq" className="hover:text-black transition-colors">Frequently Asked Questions</a></li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Section 1 */}
          <motion.div
            id="why-tax-planning"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-black mb-6">
              Why Tax Planning Matters for Billboard Investors
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Billboard investments, like other commercial real estate assets, often involve significant capital outlays and generate ongoing rental income. Understanding the tax implications can help investors make more informed decisions about acquisition timing, ownership structure, and long-term portfolio strategy.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Tax planning is not about avoiding obligations—it's about understanding the rules and structuring investments in ways that align with your financial goals. Common areas where tax planning can add value include depreciation schedules, entity selection, passive activity rules, and exit strategies.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              In Las Vegas, where billboard assets can range from small static structures to premium digital displays on high-traffic corridors, the tax treatment may vary based on asset classification, purchase price allocation, and how the investment is held (individual, LLC, partnership, etc.).
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              Working with a qualified tax professional early in the investment process can help identify opportunities and avoid common pitfalls. Tax laws change regularly, and what worked in previous years may not apply under current regulations.
            </p>
          </motion.div>

          {/* Section 2 */}
          <motion.div
            id="depreciation-basics"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-black mb-6">
              Depreciation Basics (Conceptual Overview)
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Depreciation is a tax concept that allows investors to recover the cost of income-producing property over time. For billboard structures, the IRS typically classifies them as tangible personal property or real property improvements, depending on various factors.
            </p>

            {/* Subsection 1 */}
            <h3 className="text-2xl font-bold text-black mb-4">
              How Depreciation Generally Works
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              When you purchase a billboard, the IRS often allows you to deduct a portion of the asset's cost each year over its "useful life." The useful life varies by asset type—some billboard components may qualify for shorter recovery periods (5–7 years), while others may fall under longer schedules (15–20 years or more).
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              The specific depreciation method (straight-line, accelerated, bonus depreciation) depends on the asset classification, purchase date, and current tax law provisions. These rules can change with new legislation, so it's essential to verify current regulations with a tax professional.
            </p>

            {/* Subsection 2 */}
            <h3 className="text-2xl font-bold text-black mb-4">
              Cost Segregation and Component Analysis
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Some investors work with cost segregation specialists to break down the purchase price into different components (structure, electrical systems, digital displays, land lease rights, etc.). This analysis can sometimes identify components eligible for accelerated depreciation, potentially increasing early-year deductions.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Whether cost segregation makes sense depends on the asset's purchase price, expected holding period, and your overall tax situation. The analysis itself has a cost, so investors typically evaluate whether the potential tax benefits justify the expense.
            </p>

            {/* Subsection 3 */}
            <h3 className="text-2xl font-bold text-black mb-4">
              Bonus Depreciation Considerations
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Under certain provisions of U.S. tax law, qualifying property may be eligible for bonus depreciation, which allows a larger first-year deduction. The availability and percentage of bonus depreciation can change based on legislation and phase-out schedules.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              Not all billboard assets automatically qualify for bonus depreciation—eligibility depends on factors like whether the property is "new to you," the asset classification, and the purchase date. Your tax advisor can help determine if your specific acquisition qualifies under current rules.
            </p>
          </motion.div>

          {/* Section 3 */}
          <motion.div
            id="typical-topics"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-black mb-6">
              Typical Tax Topics Investors Ask About
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Billboard investors commonly explore several tax-related questions when evaluating acquisitions. Below are some of the most frequent topics, presented with range-based language to reflect the variability in individual circumstances.
            </p>

            {/* Subsection 1 */}
            <h3 className="text-2xl font-bold text-black mb-4">
              Entity Structure and Pass-Through Taxation
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Many billboard investors hold assets through LLCs, partnerships, or S-corporations to benefit from pass-through taxation, where income and deductions flow to individual tax returns. The optimal structure depends on factors like liability protection needs, number of investors, and state tax considerations.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Different entity types have different tax filing requirements, administrative costs, and flexibility for bringing in additional investors. Consulting with both a tax advisor and attorney can help identify the structure that best fits your situation.
            </p>

            {/* Subsection 2 */}
            <h3 className="text-2xl font-bold text-black mb-4">
              Passive Activity Loss Rules
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Billboard rental income is often classified as passive income under IRS rules. If your billboard investment generates losses (due to depreciation and expenses exceeding rental income), your ability to deduct those losses against other income may be limited by passive activity loss rules.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              Some investors qualify for exceptions—such as the real estate professional designation—that can allow more favorable loss treatment. These exceptions have specific requirements regarding hours worked and material participation, so careful documentation is essential.
            </p>

            {/* Subsection 3 */}
            <h3 className="text-2xl font-bold text-black mb-4">
              1031 Exchange Opportunities
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Section 1031 of the tax code can allow investors to defer capital gains taxes when selling one investment property and acquiring another "like-kind" property. Billboard structures may qualify for 1031 exchanges, depending on how they're classified and whether they meet the like-kind requirements.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              1031 exchanges have strict timing rules and procedural requirements. Working with a qualified intermediary and tax advisor is essential to ensure compliance and avoid disqualification that could trigger immediate tax liability.
            </p>

            {/* Subsection 4 */}
            <h3 className="text-2xl font-bold text-black mb-4">
              State and Local Tax Considerations
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Nevada has no state income tax, which can be advantageous for billboard investors compared to high-tax states. However, investors should still consider property taxes, business license fees, and any local taxes that may apply to billboard operations.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              If you're an out-of-state investor, you may have additional filing requirements in your home state. Multi-state tax issues can be complex, so consulting with a tax professional familiar with both Nevada and your home state is often advisable.
            </p>
          </motion.div>

          {/* Section 4 */}
          <motion.div
            id="example-scenarios"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-black mb-6">
              Example Scenarios (Hypothetical)
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              The following scenarios are simplified examples for educational purposes only. Actual tax outcomes depend on many factors and current tax law. Always consult a qualified professional before making decisions.
            </p>

            {/* Scenario 1 */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-black mb-3">
                Scenario 1: Individual Investor Purchasing a Static Billboard
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                An individual investor purchases a static billboard structure on leased land for a mid-six-figure amount. The investor works with a CPA to allocate the purchase price between the structure, electrical components, and other elements.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Depending on the allocation and current tax law, the investor may be able to depreciate the structure over a period ranging from 5 to 20 years, depending on asset classification. If bonus depreciation is available and applicable, the investor might take a larger first-year deduction.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                The billboard generates rental income that is typically treated as passive income. If expenses and depreciation exceed income in early years, the investor may carry forward losses to offset future passive income, subject to passive activity loss rules.
              </p>
            </div>

            {/* Scenario 2 */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-black mb-3">
                Scenario 2: LLC Partnership Acquiring Multiple Digital Billboards
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                A group of investors forms an LLC to acquire a portfolio of digital billboards along Interstate 15. The LLC engages a cost segregation specialist to identify components eligible for accelerated depreciation.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                The analysis identifies digital display equipment, electrical systems, and structural components with varying depreciation schedules. The LLC may benefit from accelerated deductions in early years, which flow through to the individual partners' tax returns.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Partners must consider their individual tax situations, including whether they have sufficient passive income to utilize the deductions immediately or whether losses will be carried forward. The LLC's operating agreement should address tax allocations and distributions.
              </p>
            </div>

            {/* Scenario 3 */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-black mb-3">
                Scenario 3: 1031 Exchange from Rental Property to Billboard Asset
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                An investor sells a rental property with significant capital gains and wants to defer taxes by using a 1031 exchange to acquire a billboard asset. The investor works with a qualified intermediary to structure the exchange properly.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                The billboard must qualify as like-kind property, and the investor must meet strict timing requirements (typically 45 days to identify replacement property and 180 days to close). If executed correctly, the investor can defer capital gains taxes and carry forward the original property's tax basis.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                The investor's CPA helps ensure the exchange meets all IRS requirements and advises on how the deferred gain will be treated when the billboard is eventually sold. Proper documentation and compliance are critical to avoid disqualification.
              </p>
            </div>
          </motion.div>

          {/* Section 5 */}
          <motion.div
            id="prepare-cpa"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-black mb-6">
              What to Prepare Before Talking to a CPA
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              To get the most value from your consultation with a tax professional, it's helpful to gather relevant information and clarify your investment goals beforehand. Here's a practical checklist:
            </p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-black mb-4">Pre-Consultation Checklist</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Purchase details:</strong> Acquisition price, closing date, purchase agreement, and any seller financing terms</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Asset breakdown:</strong> Information about the billboard structure, digital equipment, electrical systems, and land lease arrangements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Income projections:</strong> Expected rental income, lease terms, and any existing advertiser contracts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Expense estimates:</strong> Anticipated operating costs, maintenance, insurance, property taxes, and management fees</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Ownership structure:</strong> How you plan to hold the asset (individual, LLC, partnership) and whether you have co-investors</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Personal tax situation:</strong> Your current income sources, tax bracket, and whether you have other passive investments</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Investment timeline:</strong> Expected holding period and exit strategy (long-term hold, eventual sale, 1031 exchange)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Prior tax returns:</strong> Recent returns showing your income, deductions, and any existing passive activities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-black font-bold mr-3">✓</span>
                  <span><strong>Questions and goals:</strong> Specific questions about depreciation, entity structure, or tax strategies you want to explore</span>
                </li>
              </ul>
            </div>

            <p className="text-base text-gray-700 leading-relaxed mt-6">
              Having this information organized will help your CPA provide more specific guidance tailored to your situation. Remember that tax planning is most effective when done proactively—ideally before you finalize the purchase.
            </p>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            id="faq"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 bg-gray-50 rounded-lg p-8"
          >
            <h2 className="text-3xl font-bold text-black mb-8">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-black mb-2">
                  What are the typical depreciation periods for billboard assets?
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Depreciation periods can vary widely depending on how the IRS classifies the asset. Billboard structures may fall under recovery periods ranging from 5 to 20 years or more, depending on whether they're treated as personal property, land improvements, or real property. A cost segregation study can help identify components with shorter recovery periods. Always consult a tax professional for guidance specific to your asset.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-black mb-2">
                  Can I use bonus depreciation on a billboard purchase?
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Bonus depreciation availability depends on current tax law, the asset's classification, and whether it qualifies as "new to you" property. The percentage of bonus depreciation and eligibility rules can change with legislation. Some billboard components may qualify while others may not. Your CPA can evaluate your specific acquisition under current regulations to determine eligibility.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-black mb-2">
                  Should I hold my billboard investment in an LLC or as an individual?
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  The optimal ownership structure depends on multiple factors including liability protection, number of investors, estate planning goals, and tax considerations. LLCs often provide liability protection and flexibility, while individual ownership may be simpler for single-owner situations. Both can offer pass-through taxation. Consult with both a tax advisor and attorney to determine the best structure for your circumstances.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-black mb-2">
                  How do passive activity loss rules affect billboard investments?
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Billboard rental income is typically classified as passive income. If your investment generates tax losses (often due to depreciation), your ability to deduct those losses against non-passive income may be limited by passive activity loss rules. Unused losses can generally be carried forward to offset future passive income. Some investors may qualify for exceptions, such as the real estate professional designation, which has specific requirements. Your tax advisor can help you understand how these rules apply to your situation.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Final Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-sm text-gray-500 italic border-t border-gray-200 pt-6"
          >
            <p className="mb-3">
              <strong>Tax Disclaimer:</strong> This article provides general educational information about common tax topics related to billboard investments. It is not tax, legal, or financial advice and should not be relied upon as such. Tax laws are complex, change frequently, and vary by jurisdiction and individual circumstances.
            </p>
            <p>
              Before making any investment or tax decisions, consult with qualified professionals including a CPA, tax attorney, and financial advisor who can evaluate your specific situation. The examples provided are hypothetical and simplified for educational purposes—actual tax outcomes will vary based on numerous factors including current law, asset classification, ownership structure, and individual tax situations.
            </p>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-black text-white rounded-lg p-10 text-center"
          >
            <h2 className="text-3xl font-bold mb-6">
              Ready to Explore Billboard Investment Opportunities?
            </h2>
            <p className="text-base text-gray-300 mb-8 max-w-2xl mx-auto">
              Learn more about Las Vegas billboard assets, calculate potential returns, or explore premium locations for your investment portfolio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/en/partnership"
                className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Explore Investment Opportunities
              </Link>
              <Link
                to="/en/simulator"
                className="inline-block px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-colors whitespace-nowrap"
              >
                ROI Calculator
              </Link>
              <Link
                to="/en/locations"
                className="inline-block px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-colors whitespace-nowrap"
              >
                View Premium Locations
              </Link>
            </div>
          </motion.div>

        </div>
      </article>

      <FooterEN />
    </div>
  );
}
